import {useEffect, useMemo, useState} from 'react';
import {useQuery} from '@apollo/client';
import {Form, InputNumber, Select, Tooltip} from 'antd';
import {toast} from 'react-toastify';
import {GET_EXCHANGE_LISTED_INSTRUMENTS} from 'graphql/queries/collections';
import styles from '../../../collectionsUI.module.scss';

/**
 * Constituent editor for the Instrument v2 collection contract.
 *
 * A constituent is a PublicEquity financial instrument, so the value stored per
 * row is `FinancialInstrument.id` — never an issuer/company id.
 *
 * Every rule is enforced here because the backend cannot report them. All five
 * constituent rejections come back as `_schema.Error is not a constructor`
 * (BACKEND_FINDINGS 2.7), so a server-side failure can never be attributed to a
 * particular row. Three more are accepted silently by the resolver
 * (BACKEND_FINDINGS 2.8) — an empty list, a `minInstrumentCount` above the
 * constituent count, and a duplicate instrument — so nothing else catches them.
 */

const PAGE_SIZE = 50;

/** `min <= default <= max`, with `max` blank meaning no ceiling. */
export const validateConstituents = (rows, {allowRemoval, minRetained}) => {
  const problems = [];
  const usable = (rows ?? []).filter(row => row?.instrumentId);

  if (!usable.length) problems.push('Add at least one constituent');

  const seen = [];
  usable.forEach(row => {
    const label = row.ticker || 'a constituent';
    if (seen.includes(row.instrumentId))
      problems.push(`${label} is listed twice — each may appear once`);
    seen.push(row.instrumentId);

    const min = Number(row.min);
    const def = Number(row.default);
    const max = row.max === null || row.max === '' ? null : Number(row.max);

    if (!row.min || min < 1)
      problems.push(`${label} needs a minimum of 1 or more`);
    if (!row.default || def < 1)
      problems.push(`${label} needs a default of 1 or more`);
    if (max !== null && max < min)
      problems.push(`${label}: maximum is below the minimum`);
    if (def < min) problems.push(`${label}: default is below the minimum`);
    if (max !== null && def > max)
      problems.push(`${label}: default is above the maximum`);
  });

  if (allowRemoval) {
    const retained = Number(minRetained);
    if (!minRetained || retained < 1)
      problems.push('Minimum retained must be 1 or more');
    else if (retained > usable.length)
      problems.push(
        `Minimum retained (${retained}) is more than the ${
          usable.length
        } constituent${usable.length === 1 ? '' : 's'} in the collection`,
      );
  }

  return problems;
};

/** Form rows -> `instruments[]` on CollectionInput. */
export const toInstrumentsInput = rows =>
  (rows ?? [])
    .filter(row => row?.instrumentId)
    .map(row => ({
      instrumentId: row.instrumentId,
      min: Number(row.min),
      default: Number(row.default),
      ...(row.max !== null && row.max !== '' && {max: Number(row.max)}),
    }));

/** `members` from the detail query -> form rows. */
export const membersToRows = members =>
  (members ?? []).map(member => ({
    instrumentId: member?.instrumentId,
    ticker: member?.instrument?.ticker,
    name: member?.instrument?.name,
    min: member?.minimumUnits,
    default: member?.defaultUnits,
    max: member?.maximumUnits ?? null,
  }));

export const CollectionConstituents = ({
  exchangeCode,
  rows,
  setRows,
  disabled,
}) => {
  const [search, setSearch] = useState('');

  const {data, loading} = useQuery(GET_EXCHANGE_LISTED_INSTRUMENTS, {
    variables: {
      exchangeCode,
      ...(search && {search}),
      page: 1,
      limit: PAGE_SIZE,
    },
    skip: !exchangeCode,
    errorPolicy: 'all',
    onError: error => toast.error(error?.message),
  });

  const listed = data?.adminGetStockExchangeListedInstruments;
  const instruments = useMemo(() => listed?.instruments ?? [], [listed]);

  // Rows already chosen cannot be chosen again — the resolver accepts a
  // duplicate and silently double-counts it.
  const taken = useMemo(
    () => (rows ?? []).map(row => row?.instrumentId).filter(Boolean),
    [rows],
  );

  useEffect(() => {
    if (!rows?.length) setRows([{}]);
  }, []);

  const updateRow = (index, changes) => {
    const next = [...rows];
    next[index] = {...next[index], ...changes};
    setRows(next);
  };

  const onInstrumentChange = (index, instrumentId) => {
    const picked = instruments.find(item => item.id === instrumentId);
    updateRow(index, {
      instrumentId,
      ticker: picked?.ticker,
      name: picked?.name,
      // Sensible starting units so a new row is valid without extra typing.
      min: rows[index]?.min ?? 1,
      default: rows[index]?.default ?? 1,
    });
  };

  const problems = validateConstituents(rows, {allowRemoval: false});

  return (
    <div className={styles.constituents}>
      <div className={styles.constituentsHead}>
        <div>
          <h4>Constituents</h4>
          <span>
            {exchangeCode
              ? `Public equities listed on ${exchangeCode}. An instrument from another exchange, or one without a live quote, is rejected.`
              : 'Choose an exchange first — constituents are scoped to it.'}
          </span>
        </div>
        <span
          className={`${styles.chip} ${
            taken.length ? styles.chipBrand : styles.chipMute
          }`}>
          {taken.length} selected
        </span>
      </div>

      <div className={styles.constituentsBody}>
        {(rows ?? []).map((row, index) => {
          const noCeiling = row?.max === null || row?.max === '';
          return (
            <div className={styles.constituentRow} key={index}>
              <div className={styles.constituentMain}>
                <Form.Item label="Instrument" style={{marginBottom: 10}}>
                  <Select
                    showSearch
                    allowClear
                    value={row?.instrumentId}
                    loading={loading}
                    disabled={!exchangeCode || disabled}
                    placeholder={
                      exchangeCode
                        ? 'Search by name or ticker'
                        : 'Select an exchange first'
                    }
                    onChange={value => onInstrumentChange(index, value)}
                    onSearch={setSearch}
                    // Search is served by the API, so keep every option.
                    filterOption={false}
                    notFoundContent={loading ? 'Searching…' : 'No matches'}>
                    {instruments.map(item => {
                      const quote = item?.latestQuote?.lastPrice;
                      const alreadyUsed =
                        taken.includes(item.id) &&
                        row?.instrumentId !== item.id;
                      return (
                        <Select.Option
                          key={item.id}
                          value={item.id}
                          disabled={!quote || !item?.exchange || alreadyUsed}>
                          {item.ticker} — {item.name}
                          {!quote
                            ? ' (no quote)'
                            : alreadyUsed
                            ? ' (already added)'
                            : ''}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Form.Item>

                <div className={styles.constituentUnits}>
                  <Form.Item label="Minimum units" className={styles.unitField}>
                    <InputNumber
                      min={1}
                      value={row?.min}
                      disabled={disabled}
                      onChange={value => updateRow(index, {min: value})}
                      style={{width: '100%'}}
                    />
                  </Form.Item>
                  <Form.Item label="Default units" className={styles.unitField}>
                    <InputNumber
                      min={1}
                      value={row?.default}
                      disabled={disabled}
                      onChange={value => updateRow(index, {default: value})}
                      style={{width: '100%'}}
                    />
                  </Form.Item>
                  <Form.Item
                    className={styles.unitField}
                    label={
                      <Tooltip title="Leave blank for no ceiling on how many units an investor may buy.">
                        Maximum units
                      </Tooltip>
                    }>
                    <InputNumber
                      min={1}
                      value={row?.max}
                      disabled={disabled}
                      placeholder="No limit"
                      onChange={value => updateRow(index, {max: value})}
                      style={{width: '100%'}}
                    />
                  </Form.Item>
                </div>

                {row?.instrumentId && (
                  <span className={styles.constituentRule}>
                    An investor buys {row?.default ?? '—'} unit
                    {Number(row?.default) === 1 ? '' : 's'} by default, between{' '}
                    {row?.min ?? '—'} and {noCeiling ? 'no limit' : row?.max}.
                  </span>
                )}
              </div>

              {rows.length > 1 && !disabled && (
                <Tooltip title="Remove constituent">
                  <button
                    type="button"
                    className={styles.constituentRemove}
                    onClick={() =>
                      setRows(
                        rows.filter((item, position) => position !== index),
                      )
                    }>
                    <TrashIcon />
                  </button>
                </Tooltip>
              )}
            </div>
          );
        })}

        {!disabled && (
          <button
            type="button"
            className={styles.addConstituent}
            onClick={() => setRows([...(rows ?? []), {}])}>
            <PlusIcon />
            Add constituent
          </button>
        )}

        {problems.length > 0 && (
          <ul className={styles.problems}>
            {problems.map(problem => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const TrashIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true">
    <path
      d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13M10 11v6M14 11v6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true">
    <path
      d="M12 5v14M5 12h14"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
    />
  </svg>
);

export default CollectionConstituents;
